import { fmtClock, fmtClockNa, timeToMinutes } from '../lib/format.js';

// ─── Day Gantt (Round 16) — single day, employees as rows, time on X ─────
// Uses the same data model as the week grid; just renders a horizontal
// timeline. Click empty area in a row → create shift at that time. Click
// a bar → edit. Operating window 5:30am–7pm per spec; we also include
// any shift outside that window so nothing's hidden.
export function BuildScheduleDayGantt({ location, dayDate, users, shifts, unavailable, crossLocation, showAsAvailability = false,
                                  isAuthorizedHere, openCreateForCell, openEdit }) {
  // Time bounds: default 5:30a–7p, but expand to fit any out-of-window shift.
  // CJ feedback 2026-06-15: Week view showed shifts but Day was empty.
  // Defensive normalization: compare only the YYYY-MM-DD portion of
  // each side so an extra time-suffix or whitespace can't silently
  // hide every shift. Both `s.shift_date` and `dayDate` are supposed
  // to be 10-char date strings already; this is belt + suspenders.
  const dayYmd = String(dayDate || '').slice(0, 10);
  const dayShifts = shifts.filter((s) => String(s.shift_date || '').slice(0, 10) === dayYmd);
  let lo = 5 * 60 + 30, hi = 19 * 60;
  for (const s of dayShifts) {
    const a = timeToMinutes(s.start_time), b = timeToMinutes(s.end_time);
    if (a != null && a < lo) lo = Math.max(0, Math.floor(a / 30) * 30);
    if (b != null && b > hi) hi = Math.min(24 * 60, Math.ceil(b / 30) * 30);
  }
  const span = Math.max(60, hi - lo);
  const tickHours = [];
  for (let h = Math.ceil(lo / 60); h <= Math.floor(hi / 60); h++) tickHours.push(h);
  function pctFor(min) { return ((min - lo) / span) * 100; }

  // Authorized roster only, alpha sorted (matches week-grid behavior).
  const roster = users.filter(isAuthorizedHere);

  // Click empty row at coordinate → derive a 30-min snapped start.
  function rowClickHandler(employeeId) {
    return (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const pct = Math.max(0, Math.min(1, x / rect.width));
      const minute = lo + pct * span;
      const snapped = Math.round(minute / 30) * 30;
      const hh = String(Math.floor(snapped / 60)).padStart(2, '0');
      const mm = String(snapped % 60).padStart(2, '0');
      const start = `${hh}:${mm}`;
      // Default 4-hour shift, capped at hi.
      const endMin = Math.min(snapped + 240, 24 * 60 - 1);
      const eh = String(Math.floor(endMin / 60)).padStart(2, '0');
      const em = String(endMin % 60).padStart(2, '0');
      openCreateForCell(employeeId, dayDate, { start_time: start, end_time: `${eh}:${em}` });
    };
  }

  return (
    <div className="card" style={{ padding: 0, overflow: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 800 }}>
        <thead>
          <tr style={{ background: '#fafbfc', borderBottom: '1px solid #e0e0e0' }}>
            <th style={{ padding: '8px', textAlign: 'left', minWidth: 200,
                          position: 'sticky', left: 0, background: '#fafbfc', zIndex: 1 }}>
              Staff
            </th>
            <th style={{ position: 'relative', padding: 0, minWidth: 600, width: '100%' }}>
              {/* CJ feedback 2026-06-15: the time-axis column was
                  collapsing to zero width because its only contents are
                  position:absolute spans (which contribute no natural
                  width). With table-layout:auto + a flex sibling that
                  has minWidth:200, the auto-sized column lost. Explicit
                  minWidth + width:100% keeps the axis (and the per-row
                  cells below) visible. */}
              <div style={{ position: 'relative', height: 28, width: '100%' }}>
                {tickHours.map((h) => (
                  <span key={h} style={{ position: 'absolute', left: `${pctFor(h * 60)}%`, transform: 'translateX(-50%)',
                                          fontSize: 10, color: '#9e9e9e', top: 8 }}>
                    {h === 12 ? '12p' : h > 12 ? `${h - 12}p` : `${h}a`}
                  </span>
                ))}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {roster.length === 0 && (
            <tr><td colSpan={2} style={{ padding: 16, color: '#9e9e9e', textAlign: 'center', fontStyle: 'italic' }}>
              No staff authorized at this location.
            </td></tr>
          )}
          {roster.map((u) => {
            // Number() on both sides — same defensive normalization as
            // the date filter above. The Week grid uses template literals
            // which coerce; strict equality here could silently miss a
            // string-vs-number employee_id mismatch.
            const userShifts = dayShifts.filter((s) => Number(s.employee_id) === Number(u.id));
            const unav = unavailable[`${u.id}|${dayDate}`] || [];
            const fullDay = unav.find((w) => w.kind === 'full_day_off' || w.kind === 'tor_full_day');
            const partials = unav.filter((w) => w.kind === 'time_range' || w.kind === 'tor_partial');
            // Migration 076: positive availability hints.
            const preferredFullDay = unav.find((w) => w.kind === 'preferred_full_day');
            const preferredWindows = unav.filter((w) => w.kind === 'preferred_window');
            const elsewhere = crossLocation[u.id] || [];
            const elseToday = elsewhere.filter((x) => x.date === dayDate);
            return (
              <tr key={u.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '8px', fontWeight: 600, position: 'sticky', left: 0, background: '#fff',
                              zIndex: 1, verticalAlign: 'top' }}>
                  <div style={{ fontSize: 13 }}>{u.name}</div>
                  <div style={{ fontSize: 10, color: '#9e9e9e' }}>{u.role}</div>
                  {elseToday.length > 0 && (
                    <div title={elseToday.map((x) => `${x.location_name} ${fmtClock(x.start_time)}–${fmtClock(x.end_time)}`).join('\n')}
                      style={{ fontSize: 9, fontWeight: 700, color: '#6a1b9a',
                                background: '#f3e5f5', border: '1px solid #ce93d8',
                                padding: '1px 5px', borderRadius: 999, marginTop: 3,
                                display: 'inline-block' }}>
                      Also at {[...new Set(elseToday.map((x) => x.location_name))].join(', ')}
                    </div>
                  )}
                </td>
                <td onClick={userShifts.length === 0 ? rowClickHandler(u.id) : undefined}
                  style={{ position: 'relative', padding: 0, height: 44,
                            // Match the time-axis th width so the
                            // column doesn't collapse — see comment on
                            // the th above.
                            minWidth: 600, width: '100%',
                            cursor: userShifts.length === 0 ? 'pointer' : 'default',
                            // Invert the row tint when the Availability
                            // toggle is on: open rows go soft green, blocked
                            // rows fade to neutral gray.
                            background: showAsAvailability
                              ? (fullDay || partials.length ? '#f3f4f6' : (userShifts.length === 0 ? '#dcfce7' : '#fff'))
                              : (fullDay ? '#fff5f5' : (partials.length ? '#fff8e1' : '#fff')) }}>
                  {/* Hour tick lines */}
                  {tickHours.map((h) => (
                    <div key={h} style={{ position: 'absolute', left: `${pctFor(h * 60)}%`,
                                          top: 0, bottom: 0, borderLeft: '1px dashed #f0f0f0' }} />
                  ))}
                  {/* Unavailable shading: full day. CJ 2026-06-15
                      followup: previously the OFF overlay disappeared
                      the moment a manager scheduled someone here — losing
                      the "you overrode their day off" signal. Now it
                      always renders; shift pills are positioned over it
                      with z-index, so the OFF stripe sits behind as a
                      permanent override warning. */}
                  {fullDay && (
                    <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0,
                                    background: userShifts.length === 0 ? 'transparent' : 'rgba(198,40,40,0.10)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 10, fontWeight: 700, color: '#c62828',
                                    textTransform: 'uppercase', letterSpacing: 0.6,
                                    pointerEvents: 'none',
                                    opacity: userShifts.length === 0 ? 1 : 0.75 }}>
                      OFF{fullDay.kind === 'tor_full_day' ? ' (TOR)' : ''}
                      {fullDay.note ? ` · ${fullDay.note}` : ''}
                    </div>
                  )}
                  {/* Migration 076: positive-availability full day —
                      soft green overlay (similar wash to the OFF
                      overlay but in green). Only renders when there's
                      no higher-priority red OFF. */}
                  {!fullDay && preferredFullDay && (
                    <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0,
                                    background: userShifts.length === 0 ? 'rgba(22,101,52,0.10)' : 'rgba(22,101,52,0.06)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 10, fontWeight: 700, color: '#166534',
                                    textTransform: 'uppercase', letterSpacing: 0.6, pointerEvents: 'none',
                                    opacity: userShifts.length === 0 ? 1 : 0.75 }}>
                      AVAIL · ALL DAY
                    </div>
                  )}
                  {/* Migration 076: positive-availability time windows —
                      green sibling of the orange NA blocks below. */}
                  {!fullDay && !preferredFullDay && preferredWindows.map((p, i) => {
                    const [s, e] = p.range.split('-');
                    const sm = timeToMinutes(s), em = timeToMinutes(e);
                    if (sm == null || em == null || em <= lo || sm >= hi) return null;
                    const left = pctFor(Math.max(sm, lo));
                    const right = pctFor(Math.min(em, hi));
                    const widthPct = right - left;
                    const labelTime = `${fmtClockNa(s)}–${fmtClockNa(e)}`;
                    return (
                      <div key={`pref-${i}`}
                        title={`Available: ${labelTime}`}
                        style={{ position: 'absolute',
                                   left: `${left}%`, width: `${widthPct}%`,
                                   top: 0, bottom: 0,
                                   background: 'rgba(22,101,52,0.18)',
                                   borderLeft: '1px solid #166534',
                                   borderRight: '1px solid #166534',
                                   display: 'flex', alignItems: 'center', justifyContent: 'center',
                                   color: '#14532d', fontSize: 10, fontWeight: 700,
                                   letterSpacing: 0.4, textTransform: 'uppercase',
                                   pointerEvents: 'none', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                        {widthPct > 12 ? `AVAIL ${labelTime}` : 'AVAIL'}
                      </div>
                    );
                  })}
                  {/* Partial reductions as solid blocks with an "NA"
                      label inside. CJ 2026-06-15: the previous faint-
                      stripe rendering was easy to miss in a busy Gantt;
                      now the block is fully shaded and labeled "NA"
                      (with the time range when the block is wide enough)
                      so the manager can decide to override knowingly. */}
                  {!fullDay && partials.map((p, i) => {
                    const [s, e] = p.range.split('-');
                    const sm = timeToMinutes(s), em = timeToMinutes(e);
                    if (sm == null || em == null || em <= lo || sm >= hi) return null;
                    const left = pctFor(Math.max(sm, lo));
                    const right = pctFor(Math.min(em, hi));
                    const widthPct = right - left;
                    const labelTime = `${fmtClockNa(s)}–${fmtClockNa(e)}`;
                    return (
                      <div key={i}
                        title={`Not available: ${labelTime}`}
                        style={{ position: 'absolute',
                                   left: `${left}%`, width: `${widthPct}%`,
                                   top: 0, bottom: 0,
                                   background: 'rgba(255,167,38,0.35)',
                                   borderLeft: '1px solid #f59e0b',
                                   borderRight: '1px solid #f59e0b',
                                   display: 'flex', alignItems: 'center', justifyContent: 'center',
                                   color: '#7c2d12', fontSize: 10, fontWeight: 700,
                                   letterSpacing: 0.4, textTransform: 'uppercase',
                                   pointerEvents: 'none', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                        {widthPct > 12 ? `NA ${labelTime}` : 'NA'}
                      </div>
                    );
                  })}
                  {/* Cross-location shaded blocks */}
                  {elseToday.map((x, i) => {
                    const sm = timeToMinutes(x.start_time), em = timeToMinutes(x.end_time);
                    if (sm == null || em == null) return null;
                    const left = pctFor(Math.max(sm, lo));
                    const right = pctFor(Math.min(em, hi));
                    if (right - left < 1) return null;
                    return (
                      <div key={`else-${i}`} title={`${x.location_name} ${fmtClock(x.start_time)}–${fmtClock(x.end_time)}`}
                        style={{ position: 'absolute', left: `${left}%`, width: `${right - left}%`,
                                  top: 6, bottom: 6,
                                  background: 'repeating-linear-gradient(135deg, rgba(106,27,154,0.15), rgba(106,27,154,0.15) 6px, rgba(106,27,154,0.06) 6px, rgba(106,27,154,0.06) 12px)',
                                  border: '1px dashed #ce93d8', borderRadius: 4,
                                  pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: 9, color: '#6a1b9a', fontWeight: 700 }}>
                        @ {x.location_name}
                      </div>
                    );
                  })}
                  {/* Shift bars — drafts render faded + dashed + striped so
                      published vs unpublished is obvious at a glance. */}
                  {userShifts.map((s) => {
                    const sm = timeToMinutes(s.start_time), em = timeToMinutes(s.end_time);
                    if (sm == null || em == null) return null;
                    const left = pctFor(Math.max(sm, lo));
                    const right = pctFor(Math.min(em, hi));
                    const isSquare = s.source === 'square';
                    const hasWarn = s.warnings && s.warnings.length > 0;
                    const isPublished = s.status === 'published';
                    const baseBg = isSquare ? '#eceff1' : '#e8f5e9';
                    const fg = isSquare ? '#37474f' : '#1b5e20';
                    const border = hasWarn ? '#f9a825' : (isSquare ? '#cfd8dc' : '#a5d6a7');
                    const draftStripe = `repeating-linear-gradient(135deg, ${baseBg}, ${baseBg} 4px, rgba(255,255,255,0.55) 4px, rgba(255,255,255,0.55) 8px)`;
                    return (
                      <div key={s.id} onClick={(e) => { e.stopPropagation(); openEdit(s); }}
                        title={`${isPublished ? 'Published' : 'Draft'} · ${fmtClock(s.start_time)}–${fmtClock(s.end_time)}${s.role ? ' · ' + s.role : ''}${s.notes ? ' — ' + s.notes : ''}`}
                        style={{ position: 'absolute', left: `${left}%`, width: `${right - left}%`,
                                  top: 4, bottom: 4,
                                  background: isPublished ? baseBg : draftStripe,
                                  color: fg,
                                  border: `1.5px ${isPublished ? 'solid' : 'dashed'} ${border}`,
                                  opacity: isPublished ? 1 : 0.78,
                                  borderRadius: 5,
                                  fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                                  whiteSpace: 'nowrap', overflow: 'hidden', padding: '0 6px' }}>
                        <span>{fmtClock(s.start_time)}–{fmtClock(s.end_time)}</span>
                        {hasWarn && <span style={{ color: '#ef6c00' }}>⚠</span>}
                        {isSquare && <span style={{ fontSize: 9, color: '#9e9e9e' }}>SQ</span>}
                        {isPublished && !isSquare && <span style={{ fontSize: 9, color: '#1b5e20' }}>✓</span>}
                      </div>
                    );
                  })}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
